import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import {
  Button,
  Card,
  Chip,
  ConfirmDeleteModal,
  EmptyState,
  Input,
  Modal,
  ProviderTag,
  Skeleton,
  StatusChip,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import type { ChipProps } from '../components/ui'
import { useAvailableSites, useEdgeGateways, useLocationBindings, useLocationTree, useManagedSites, useProviderCapabilities, useSyncRuns, useSystems } from '../api/hooks'
import { api, ApiError } from '../api/client'
import type { AccessSystem, CreateSystemBody, LocationBinding, LocationNode, SyncRun, SystemProvider } from '../api/types'
import { useAuthStore } from '../stores/auth'
import { humanDurationSince, relativeTime, shortDateTime } from '../lib/format'
import { canonicalTimeZone, timeZoneNames } from '../lib/timezones'
import { IntegrationsCard } from '../components/features/IntegrationsCard'
import { FlexCProvisioningCard } from '../components/features/FlexCProvisioningCard'
import { MappingCallout } from '../components/features/MappingCallout'
import { MappingCard } from '../components/features/MappingCard'
import { DoorControllersCard } from '../components/features/DoorControllersCard'
import { VendorDataCard } from '../components/features/VendorDataCard'
import { ForwardingDefaultsCard } from '../components/features/ForwardingDefaultsCard'
import { TlsPinCard } from '../components/features/TlsPinCard'
import { LocationLink } from '../components/LocationLink'

const providerNames: Record<SystemProvider, string> = {
  axema: 'Axema Vaka',
  techfass: 'TECHFASS',
  acre: 'Acre / SPC',
  came: 'CAME BPT',
  wisenet: 'Wisenet WAVE',
}

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

const runTones: Record<string, ChipProps['tone']> = {
  completed: 'ok',
  running: 'info',
  failed: 'danger',
  partial: 'warn',
  skipped: 'muted',
}

/** What the vendor-side entity a bound location becomes, per provider. */
const bindingExplainers: Partial<Record<SystemProvider, string>> = {
  axema: 'Locations become organisation groups on this panel.',
  techfass: 'Locations become folders on the badge portal.',
  came: 'Each person with an occupancy under a CAME device gets their own unit and address-book entry there.',
}

const bindingKindLabels: Record<LocationBinding['kind'], string> = {
  org: 'org',
  folder: 'folder',
  unit: 'unit',
}

/** Binding state is reconciled asynchronously — read values defensively. */
const bindingStateTones: Record<string, ChipProps['tone']> = {
  dirty: 'warn',
  pending: 'warn',
  synced: 'ok',
  idle: 'ok',
  blocked: 'danger',
  error: 'danger',
}

/** Pre-order flatten for the bind-location picker (`— `-indented by depth,
 *  the same tree-label idiom the other location pickers use; 🔒 marks a
 *  grant-inheritance barrier). */
function flattenTree(nodes: LocationNode[], depth = 0): { uid: string; name: string; depth: number; barrier: boolean }[] {
  return nodes.flatMap((node) => [
    { uid: node.unique_id, name: node.name, depth, barrier: node.inherits_grants === false },
    ...flattenTree(node.children, depth + 1),
  ])
}

function duration(start: string | null, end: string | null): string {
  if (!start || !end) return '—'
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (Number.isNaN(ms) || ms < 0) return '—'
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${s % 60}s`
}

function runDeltas(r: SyncRun): string {
  return `+${r.items_added} ±${r.items_changed} −${r.items_deleted}`
}

/** Providers whose connection needs a base URL (others are fixed cloud services). */
const NEEDS_ENDPOINT: ReadonlySet<string> = new Set(['axema', 'acre', 'wisenet'])

/** Fixed cloud endpoints the backend assumes when the field is left blank. */
const ENDPOINT_DEFAULTS: Readonly<Record<string, string>> = {
  acre: 'https://www.acreintrusion.cloud',
}

/** ACRE panels report local wall-clock times; the backend reads them in this zone when unset. */
const DEFAULT_PANEL_TIMEZONE = 'Europe/Stockholm'

/**
 * Edit-connection form (mounted fresh per open, so state initializes from
 * `system`). Secrets are write-only and NEVER rendered: all credential
 * fields start blank, and submitting with every field blank omits `secrets`
 * from the PUT so the backend preserves the stored credentials. Filling
 * any field replaces the whole set (main user + secret then required).
 */
function EditSystemForm({
  system,
  onDone,
}: {
  system: AccessSystem
  onDone: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const [name, setName] = useState(system.name)
  const [enabled, setEnabled] = useState(system.enabled)
  const [dryRun, setDryRun] = useState(system.dry_run)
  const [baseUrl, setBaseUrl] = useState(
    typeof system.config.base_url === 'string' ? system.config.base_url : '',
  )
  const zoneNames = useMemo(() => timeZoneNames(), [])
  const [panelTimezone, setPanelTimezone] = useState(
    typeof system.config.timezone === 'string' ? system.config.timezone : '',
  )
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [celeryUsername, setCeleryUsername] = useState('')
  const [celeryPassword, setCeleryPassword] = useState('')
  const [wsUsername, setWsUsername] = useState('')
  const [wsPassword, setWsPassword] = useState('')

  const update = useMutation({
    mutationFn: (body: CreateSystemBody) => api.updateSystem(system.unique_id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope] })
      onDone()
    },
  })

  const secretTouched = [username, password, celeryUsername, celeryPassword, wsUsername, wsPassword].some(
    (v) => v.length > 0,
  )
  const secretsValid = !secretTouched || (username.trim().length > 0 && password.length > 0)

  function submit() {
    const config = { ...system.config }
    delete config.allow_writes
    if (NEEDS_ENDPOINT.has(system.provider)) {
      if (baseUrl.trim()) config.base_url = baseUrl.trim()
      else delete config.base_url
    }
    if (system.provider === 'acre') {
      const zone = canonicalTimeZone(panelTimezone, zoneNames)
      if (zone) config.timezone = zone
      else delete config.timezone
    }
    const body: CreateSystemBody = {
      provider: system.provider,
      name: name.trim() || system.name,
      enabled,
      dry_run: dryRun,
      config,
      reach: system.reach,
      gateway: system.gateway,
    }
    if (secretTouched) {
      body.secrets =
        system.provider === 'axema'
          ? {
              main: { username: username.trim(), password },
              ...(celeryUsername.trim()
                ? { celery: { username: celeryUsername.trim(), password: celeryPassword } }
                : {}),
              ...(wsUsername.trim()
                ? { websocket: { username: wsUsername.trim(), password: wsPassword } }
                : {}),
            }
          : { username: username.trim(), password }
    }
    update.mutate(body)
  }

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (secretsValid && !update.isPending) submit()
      }}
    >
      <Input label="Installation name" value={name} onChange={(e) => setName(e.target.value)} />
      {NEEDS_ENDPOINT.has(system.provider) && (
        <div className="flex flex-col gap-1.5">
          <Input
            label="API endpoint"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder={ENDPOINT_DEFAULTS[system.provider] ?? 'https://…'}
            inputMode="url"
          />
          {ENDPOINT_DEFAULTS[system.provider] && !baseUrl.trim() && (
            <p className="text-xs text-ostrel-ink-muted">
              Blank uses the default: {ENDPOINT_DEFAULTS[system.provider]}
            </p>
          )}
          {system.tls_pin && baseUrl.trim() !== (system.config.base_url ?? '') && (
            <p className="text-xs text-ostrel-warn">
              The TLS certificate pin is kept — connections fail until the new server's certificate
              is fetched and pinned on the TLS certificate card.
            </p>
          )}
        </div>
      )}
      {system.provider === 'acre' && (
        <div className="flex flex-col gap-1.5">
          <Input
            label="Panel time zone"
            value={panelTimezone}
            onChange={(e) => setPanelTimezone(e.target.value)}
            placeholder={DEFAULT_PANEL_TIMEZONE}
            autoComplete="off"
            spellCheck={false}
            list="acre-panel-timezones"
          />
          <datalist id="acre-panel-timezones">
            {zoneNames.map((z) => (
              <option key={z} value={z} />
            ))}
          </datalist>
          <p className="text-xs text-ostrel-ink-muted">
            IANA name of the zone the panels' clocks run in, e.g. Europe/Oslo. Event times are read in
            it; changing it re-reads the stored events in the new zone.
            {!panelTimezone.trim() && ` Blank uses the default: ${DEFAULT_PANEL_TIMEZONE}.`}
          </p>
        </div>
      )}
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-6">
          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ostrel-ink">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-3.5 w-3.5 accent-ostrel-gold"
            />
            Enabled
          </label>
          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ostrel-ink">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="h-3.5 w-3.5 accent-ostrel-gold"
            />
            Dry run
          </label>
        </div>
        <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
          <span className="font-medium text-ostrel-ink">Dry run</span> simulates all vendor writes; nothing
          is pushed to the system. Uncheck to allow managed bindings to write.
        </p>
        {!system.dry_run && dryRun && (
          <p className="text-[11px] leading-relaxed text-ostrel-warn">
            You are enabling dry run. Any pending managed writes will be recorded as no-op syncs until dry
            run is turned off.
          </p>
        )}
      </div>

      <div className="rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-4 py-3">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
          Credentials
        </p>
        <p className="mb-3 text-xs leading-relaxed text-ostrel-ink-muted">
          Stored credentials are never shown. Leave every field blank to keep them; fill in to
          replace the whole set.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="API user"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="••••••••"
            autoComplete="off"
          />
          <Input
            label="Secret"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />
          {system.provider === 'axema' && (
            <>
              <Input
                label="Sync user (celery)"
                value={celeryUsername}
                onChange={(e) => setCeleryUsername(e.target.value)}
                placeholder="••••••••"
                autoComplete="off"
              />
              <Input
                label="Sync secret"
                type="password"
                value={celeryPassword}
                onChange={(e) => setCeleryPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <Input
                label="Listener user (websocket)"
                value={wsUsername}
                onChange={(e) => setWsUsername(e.target.value)}
                placeholder="••••••••"
                autoComplete="off"
              />
              <Input
                label="Listener secret"
                type="password"
                value={wsPassword}
                onChange={(e) => setWsPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </>
          )}
        </div>
      </div>

      {secretTouched && !secretsValid && (
        <p className="text-xs text-ostrel-danger">
          Replacing credentials requires at least the API user and its secret.
        </p>
      )}
      {update.error && (
        <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {errDetail(update.error)}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" variant="navy" size="sm" disabled={!secretsValid || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
      {secretTouched && (
        <p className="text-right text-[11px] text-ostrel-ink-muted">
          Re-verify the connection after changing credentials.
        </p>
      )}
    </form>
  )
}

export default function SystemDetailPage() {
  const { uid } = useParams<{ uid: string }>()
  const navigate = useNavigate()
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const isAdmin = useAuthStore((s) => s.customer?.role === 'admin')
  const queryClient = useQueryClient()

  const systems = useSystems()
  const system = systems.data?.find((s) => s.unique_id === uid)
  const sites = useAvailableSites(system?.unique_id)
  const managed = useManagedSites(system?.unique_id)
  const runs = useSyncRuns(uid)
  // Location provisioning is a vendor write-back: acre has none, and a
  // Wisenet WAVE system only places cameras and forwards events.
  const supportsBindings = !!system && system.provider !== 'acre' && system.provider !== 'wisenet'
  const bindings = useLocationBindings(supportsBindings ? system.unique_id : undefined)
  const locationTree = useLocationTree()
  const gateways = useEdgeGateways()
  /** Display name of the linked gateway appliance (uid fallback in the JSX). */
  const gatewayName = system?.gateway
    ? gateways.data?.find((g) => g.unique_id === system.gateway)?.name
    : undefined

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [selectionDirty, setSelectionDirty] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [bindLocation, setBindLocation] = useState('')
  const [confirmUnbind, setConfirmUnbind] = useState<LocationBinding | null>(null)

  // Pre-check the saved selection once it loads (until the operator edits it).
  useEffect(() => {
    if (!selectionDirty && managed.data) {
      setSelected(new Set(managed.data.map((s) => s.site_key)))
    }
  }, [managed.data, selectionDirty])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope] })
  const verify = useMutation({ mutationFn: api.verifySystem, onSuccess: invalidate })
  // The provider matrix drives the sync affordance — no provider checks.
  const providerCaps = useProviderCapabilities()
  const syncKinds = providerCaps.data?.[system?.provider ?? '']?.sync_kinds ?? []
  // A provider only exposes `capabilities` when it's SupportsReconcile
  // (core/capabilities.py's invariant) — reuse that as the Push-now
  // affordance gate, same "never a provider check" discipline as syncKinds.
  const reconcileCapable = (providerCaps.data?.[system?.provider ?? '']?.capabilities ?? []).length > 0
  // Brief inline confirmation after a manual pull/push enqueues (cleared on
  // a timer — the run itself lands in the sync history once a worker picks
  // it up).
  const [pullStarted, setPullStarted] = useState(false)
  const pull = useMutation({
    mutationFn: (s: AccessSystem) => api.syncSystemNow(s.unique_id),
    onSuccess: () => {
      invalidate()
      setPullStarted(true)
      window.setTimeout(() => setPullStarted(false), 4000)
    },
  })
  const [pushStarted, setPushStarted] = useState(false)
  const push = useMutation({
    mutationFn: (s: AccessSystem) => api.pushSystemNow(s.unique_id),
    onSuccess: () => {
      invalidate()
      setPushStarted(true)
      window.setTimeout(() => setPushStarted(false), 4000)
    },
  })
  const saveSites = useMutation({
    mutationFn: (body: { site_key: string; label: string }[]) =>
      api.setManagedSites(uid as string, body),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: api.deleteSystem,
    onSuccess: () => {
      invalidate()
      navigate('/systems')
    },
  })
  const bind = useMutation({
    mutationFn: (location: string) => api.bindLocation(uid as string, { location }),
    onSuccess: () => {
      invalidate()
      setBindLocation('')
    },
  })
  const unbind = useMutation({
    mutationFn: (bindingUid: string) => api.unbindLocation(uid as string, bindingUid),
    onSuccess: () => {
      invalidate()
      setConfirmUnbind(null)
    },
  })
  const actionError =
    verify.error ?? pull.error ?? push.error ?? saveSites.error ?? remove.error ?? bind.error ?? unbind.error

  const siteLabels = useMemo(
    () => new Map((sites.data ?? []).map((s) => [s.site_key, s.label || s.site_key])),
    [sites.data],
  )

  // Already-bound locations are excluded from the picker (re-POST is idempotent
  // anyway, but hiding them keeps the list short).
  const bindOptions = useMemo(() => {
    const bound = new Set((bindings.data ?? []).map((b) => b.location))
    return flattenTree(locationTree.data ?? []).filter((o) => !bound.has(o.uid))
  }, [locationTree.data, bindings.data])

  // Available sites from the vendor PLUS any saved selection the vendor
  // didn't return this time (unreachable/renamed) — saved rows must stay
  // visible so they can be unchecked.
  const displaySites = useMemo(() => {
    const available = sites.data ?? []
    const seen = new Set(available.map((s) => s.site_key))
    const savedOnly = (managed.data ?? []).filter((s) => !seen.has(s.site_key))
    return [...available, ...savedOnly]
  }, [sites.data, managed.data])

  function toggleSite(key: string) {
    setSelectionDirty(true)
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function saveSelection() {
    const body = [...selected].map((key) => ({ site_key: key, label: siteLabels.get(key) ?? key }))
    saveSites.mutate(body, { onSuccess: () => setSelectionDirty(false) })
  }

  if (systems.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-36" />
        <Skeleton className="h-56" />
      </div>
    )
  }

  if (systems.isError) {
    return <EmptyState title="Could not load systems" hint={systems.error.message} />
  }

  if (!system) {
    return (
      <EmptyState
        title="System not found"
        hint={`No system with id ${uid ?? ''} on this customer.`}
        action={
          <Button variant="ghost" onClick={() => navigate('/systems')}>
            ← Back to systems
          </Button>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Connections"
        title={system.name}
        action={
          <>
            <Button
              variant="ghost"
              disabled={verify.isPending}
              onClick={() => verify.mutate(system.unique_id)}
            >
              Verify
            </Button>
            {syncKinds.length > 0 && (
              <Button
                variant="navy"
                disabled={pull.isPending || !system.enabled}
                title={!system.enabled ? 'System is disabled — enable it to pull' : 'Vendor → us: refresh our mirror from the vendor'}
                onClick={() => pull.mutate(system)}
              >
                {pull.isPending ? 'Pulling…' : 'Pull now'}
              </Button>
            )}
            {reconcileCapable && (
              <Button
                variant="ghost"
                disabled={push.isPending || !system.enabled}
                title={!system.enabled ? 'System is disabled — enable it to push' : 'Us → vendor: push any pending canonical changes now'}
                onClick={() => push.mutate(system)}
              >
                {push.isPending ? 'Pushing…' : 'Push now'}
              </Button>
            )}
            {isAdmin && (
              <Button variant="ghost" onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            )}
            {isAdmin && (
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            )}
          </>
        }
      >
        <p className="tnum mt-1 text-xs text-ostrel-ink-muted">
          <Link to="/systems" className="hover:text-ostrel-gold">
            Systems
          </Link>
          {' / '}
          {system.unique_id}
        </p>
      </PageHeader>

      {/* Consecutive-failure escalation: the connector has been failing for
          a while — say so loudly, with the streak's retry cadence. */}
      {(system.consecutive_sync_failures ?? 0) >= 3 && (
        <div className="rounded-sm border border-ostrel-warn/50 bg-ostrel-warn/10 px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-warn">Offline</p>
          <p className="mt-1 text-xs text-ostrel-ink">
            {system.failing_since
              ? `Offline since ${shortDateTime(system.failing_since)} (${humanDurationSince(system.failing_since)})`
              : `${system.consecutive_sync_failures} consecutive sync failures`}
            {system.last_sync_error ? ` — ${system.last_sync_error}` : ''} — retrying{' '}
            {(system.consecutive_sync_failures ?? 0) >= 20 ? 'every 15 minutes' : 'every minute'}
          </p>
        </div>
      )}

      {pullStarted && (
        <p className="rounded-sm border border-ostrel-ok/40 bg-ostrel-ok/10 px-3 py-2 text-xs text-ostrel-ok">
          Pull started — the run lands in the sync history below.
        </p>
      )}
      {pushStarted && (
        <p className="rounded-sm border border-ostrel-ok/40 bg-ostrel-ok/10 px-3 py-2 text-xs text-ostrel-ok">
          Push started — the run lands in the sync history below.
        </p>
      )}

      {actionError && (
        <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {errDetail(actionError)}
        </p>
      )}

      <MappingCallout
        system={system}
        onOpen={() => navigate(`/systems/${system.unique_id}/mapping`)}
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <ProviderTag provider={system.provider} />
          <span className="text-sm font-medium text-ostrel-ink">
            {providerNames[system.provider] ?? system.provider}
          </span>
          <StatusChip status={system.status} />
          {system.enabled ? <Chip tone="ok" label="Enabled" /> : <Chip tone="muted" label="Disabled" />}
          {system.dry_run ? (
            <Chip tone="gold" label="Dry run" />
          ) : (
            <Chip tone="ok" label="Writable" />
          )}
          {system.duplicate_of && (
            <Chip tone="warn" label={`Duplicate of ${system.duplicate_of}`} />
          )}
        </div>
        {system.status_detail && (
          <p className="mt-2.5 text-xs text-ostrel-ink-muted">{system.status_detail}</p>
        )}
        <p className="mt-2.5 flex flex-wrap items-center gap-1.5 text-xs text-ostrel-ink-muted">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Reach
          </span>
          {system.reach === 'direct' && <span>Direct — the cloud reaches the system</span>}
          {system.reach === 'tunnel' && (
            <span className="flex items-center gap-1.5">
              Via appliance
              {system.gateway ? (
                <Link
                  to={`/gateways/${system.gateway}`}
                  className="tnum font-mono text-[11px] text-ostrel-gold hover:underline"
                >
                  {gatewayName ? `${gatewayName} (${system.gateway})` : system.gateway}
                </Link>
              ) : (
                <span className="text-ostrel-danger">— no gateway linked</span>
              )}
            </span>
          )}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-ostrel-line pt-3">
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Capabilities
          </span>
          {system.capabilities.length === 0 ? (
            <span className="text-xs text-ostrel-ink-muted">—</span>
          ) : (
            system.capabilities.map((c) => <Chip key={c} tone="muted" label={c} />)
          )}
          <span className="tnum ml-auto text-[11px] text-ostrel-ink-muted">
            {system.sync_policy?.incremental_seconds
              ? `polling ${system.sync_policy.incremental_seconds}s`
              : (system.gateway ?? 'no gateway')}
          </span>
        </div>
      </Card>

      <Card
        title="Managed sites"
        action={
          sites.data && sites.data.length > 0 ? (
            <span className="flex items-center gap-3">
              <span className="tnum text-[11px] text-ostrel-ink-muted">
                {selected.size} selected
              </span>
              <Button
                variant="navy"
                size="sm"
                disabled={saveSites.isPending || !isAdmin}
                title={isAdmin ? undefined : 'Requires the admin role'}
                onClick={saveSelection}
              >
                Save
              </Button>
            </span>
          ) : undefined
        }
      >
        {sites.isPending ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
          </div>
        ) : sites.isError && displaySites.length === 0 ? (
          <EmptyState
            title="Could not list sites"
            hint={errDetail(sites.error)}
            action={
              <Button variant="ghost" size="sm" onClick={() => sites.refetch()}>
                Retry
              </Button>
            }
          />
        ) : displaySites.length === 0 ? (
          <EmptyState
            title="No installations visible"
            hint="The connector account reports no sites. Verify credentials first."
          />
        ) : (
          <>
            <ul className="flex flex-col gap-2.5">
              {displaySites.map((site) => {
                const checked = selected.has(site.site_key)
                return (
                  <li key={site.site_key}>
                    <label
                      className={`flex cursor-pointer items-center gap-3 rounded-sm border px-3.5 py-3 transition-colors ${
                        checked
                          ? 'border-ostrel-gold bg-ostrel-sand'
                          : 'border-ostrel-line bg-ostrel-card hover:bg-ostrel-sand'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSite(site.site_key)}
                        className="h-3.5 w-3.5 accent-ostrel-gold"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ostrel-ink">
                          {site.label || site.site_key}
                        </span>
                        <span className="tnum block font-mono text-[11px] text-ostrel-ink-muted">
                          {site.site_key}
                        </span>
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
            <p className="mt-3 text-[11px] leading-relaxed text-ostrel-ink-muted">
              Saving replaces the managed-site selection. The saved selection is pre-checked;
              sites the vendor didn't return this time stay listed so they can be removed.
              Selecting at least one site flips the connector to active.
            </p>
          </>
        )}
      </Card>

      {supportsBindings && (
        <Card title="Location provisioning">
          <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
            {bindingExplainers[system.provider]}{' '}
            {system.provider === 'came'
              ? 'Units follow occupancies — add or end an occupancy to change them.'
              : 'Binding is asynchronous — the connector creates or renames the vendor entity on its next reconcile pass.'}
          </p>

          {system.provider !== 'came' && (
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <select
                aria-label="Location to bind"
                value={bindLocation}
                onChange={(e) => setBindLocation(e.target.value)}
                className="min-w-52 rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
              >
                <option value="">— pick a location</option>
                {bindOptions.map((o) => (
                  <option key={o.uid} value={o.uid}>
                    {'— '.repeat(o.depth)}
                    {o.name}
                    {o.barrier ? ' 🔒' : ''}
                  </option>
                ))}
              </select>
              <Button
                variant="navy"
                size="sm"
                disabled={!bindLocation || bind.isPending}
                onClick={() => bind.mutate(bindLocation)}
              >
                {bind.isPending ? 'Binding…' : 'Bind'}
              </Button>
            </div>
          )}

          {bindings.isPending ? (
            <div className="mt-4 flex flex-col gap-2">
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
            </div>
          ) : bindings.isError ? (
            <p className="mt-4 text-xs text-ostrel-danger">{errDetail(bindings.error)}</p>
          ) : (bindings.data?.length ?? 0) === 0 ? (
            <p className="mt-4 text-xs text-ostrel-ink-muted">
              {system.provider === 'came'
                ? 'No units yet. People with an occupancy under a CAME device appear here.'
                : 'No locations bound yet. Bound locations are provisioned on the vendor system.'}
            </p>
          ) : (
            <ul className="mt-4 flex flex-col gap-2">
              {bindings.data?.map((b) => (
                <li
                  key={b.unique_id}
                  className="flex flex-wrap items-center gap-2 rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2.5"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-ostrel-ink">
                      {b.contact_name ?? b.location_name}
                    </span>
                    {b.contact_name && (
                      <span className="block truncate text-[11px] text-ostrel-ink-muted">{b.location_name}</span>
                    )}
                    {b.device_name && (
                      <span className="block truncate text-[11px] text-ostrel-ink-muted">
                        {b.device_name}
                      </span>
                    )}
                    <span className="tnum block font-mono text-[11px] text-ostrel-ink-muted">
                      <LocationLink uid={b.location}>{b.location}</LocationLink>
                    </span>
                  </span>
                  <Chip tone="gold" label={bindingKindLabels[b.kind] ?? b.kind} />
                  <Chip tone={bindingStateTones[b.state] ?? 'muted'} label={b.state} />
                  <span className="tnum ml-auto text-[11px] text-ostrel-ink-muted">
                    {b.state === 'blocked'
                      ? 'blocked'
                      : b.state === 'dirty'
                        ? 'pending sync'
                        : b.synced_at
                          ? `synced ${relativeTime(b.synced_at)}`
                          : 'not synced yet'}
                  </span>
                  {b.kind !== 'unit' && (
                    <Button variant="danger" size="sm" onClick={() => setConfirmUnbind(b)}>
                      Unbind
                    </Button>
                  )}
                  {b.last_error && (
                    <p className="w-full text-[11px] text-ostrel-danger">{b.last_error}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <Card
        title="Sync history"
        action={
          <span className="tnum text-[11px] text-ostrel-ink-muted">
            Last sync: {system.last_sync_at ? relativeTime(system.last_sync_at) : 'never'}
          </span>
        }
      >
        {runs.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : runs.isError ? (
          <EmptyState title="Could not load sync runs" hint={runs.error.message} />
        ) : (runs.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No sync runs yet"
            hint="Runs appear once the connector is active and starts syncing."
          />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Kind</TH>
                <TH>Status</TH>
                <TH>Started</TH>
                <TH>Duration</TH>
                <TH className="text-right">Items</TH>
              </tr>
            </THead>
            <TBody>
              {runs.data?.items.map((r) => (
                <TR key={r.unique_id}>
                  <TD className="text-sm">{r.kind}</TD>
                  <TD>
                    <Chip tone={runTones[r.status] ?? 'muted'} label={r.status} />
                  </TD>
                  <TD className="tnum text-xs text-ostrel-ink-muted">
                    {r.started_at ? relativeTime(r.started_at) : '—'}
                  </TD>
                  <TD className="tnum text-xs text-ostrel-ink-muted">
                    {duration(r.started_at, r.finished_at)}
                  </TD>
                  <TD className="tnum text-right text-xs">{runDeltas(r)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <MappingCard
        system={system}
        onOpen={() => navigate(`/systems/${system.unique_id}/mapping`)}
      />

      {system.provider === 'wisenet' && <ForwardingDefaultsCard systemUid={system.unique_id} />}

      {system.tls_pinnable &&
        typeof system.config.base_url === 'string' &&
        system.config.base_url && <TlsPinCard system={system} />}

      <IntegrationsCard system={system} />

      <DoorControllersCard system={system} />

      <VendorDataCard system={system} />

      <FlexCProvisioningCard system={system} />

      {editOpen && (
        <Modal open title="Edit connection" onClose={() => setEditOpen(false)}>
          <EditSystemForm system={system} onDone={() => setEditOpen(false)} />
        </Modal>
      )}

      <ConfirmDeleteModal
        open={confirmDelete}
        title="Delete system"
        objectName={system.name}
        expected={system.unique_id}
        pending={remove.isPending}
        error={remove.error ? errDetail(remove.error) : null}
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete system'}
        onConfirm={() => remove.mutate(system.unique_id)}
        onClose={() => setConfirmDelete(false)}
      >
        <p>
          Remove <strong>{system.name}</strong> ({system.unique_id})? Managed sites, credentials
          and sync configuration are deleted; synced doors and people are kept.
        </p>
      </ConfirmDeleteModal>

      <ConfirmDeleteModal
        open={confirmUnbind !== null}
        title="Unbind location"
        objectName={confirmUnbind?.location_name ?? ''}
        expected={confirmUnbind?.unique_id ?? ''}
        pending={unbind.isPending}
        error={unbind.error ? errDetail(unbind.error) : null}
        confirmLabel={unbind.isPending ? 'Unbinding…' : 'Unbind'}
        onConfirm={() => confirmUnbind && unbind.mutate(confirmUnbind.unique_id)}
        onClose={() => setConfirmUnbind(null)}
      >
        <p>
          Unbind <strong>{confirmUnbind?.location_name}</strong> from this system? Only the
          binding is removed — the vendor entity it created is left in place.
        </p>
      </ConfirmDeleteModal>
    </div>
  )
}
